import { type MousePosT } from '../../../providers/stage-provider';
import { type RectDataT } from '../../../types';
import { VERTEX_TYPE } from '../shape/vertex';
import { BOX_BUFFER } from '../utils';
export const getTranslatedRectangleData = (deltaX: number, deltaY: number, rectangle: RectDataT): RectDataT => {
  return {
    ...rectangle,
    x: rectangle.x + deltaX,
    y: rectangle.y + deltaY
  };
};
export const getTransformedRectDataPointsOnVertexMove = (rectangle: RectDataT, newPos: MousePosT, selectedVertexType: (typeof VERTEX_TYPE)[keyof typeof VERTEX_TYPE], multiSelectBox: RectDataT | null | undefined = null): RectDataT => {
  let {
    x,
    y,
    width,
    height,
    rotationAngle
  } = rectangle;
  const checkBoundsFromRightChange = () => {
    if (!multiSelectBox) return;
    const inverted = rectangle.x > multiSelectBox.x + multiSelectBox.width;
    if (inverted) {
      x = multiSelectBox.x + multiSelectBox.width - BOX_BUFFER / 2;
      width = 1;
    } else {
      const leftSmallerThanBoxLeft = x - BOX_BUFFER / 2 < multiSelectBox.x;
      const innerRectInverted = width < 0;
      if (leftSmallerThanBoxLeft) {
        x = Math.max(rectangle.x, multiSelectBox.x + BOX_BUFFER / 2);
      } else if (innerRectInverted) {
        x = x - width;
      }
      width = Math.max(width, 1);
    }
  };
  const checkBoundsFromLeftChange = () => {
    if (!multiSelectBox) return;
    const inverted = rectangle.x + rectangle.width < multiSelectBox.x;
    if (inverted) {
      x = multiSelectBox.x + BOX_BUFFER / 2;
      width = 1;
    } else {
      const rightGreaterThanBoxRight = x + width > multiSelectBox.x + multiSelectBox.width;
      width = Math.max(width, 1);
      if (rightGreaterThanBoxRight) {
        x = Math.min(multiSelectBox.x + multiSelectBox.width - width - BOX_BUFFER / 2, x);
      }
    }
  };
  const checkBoundsFromTopChange = () => {
    if (!multiSelectBox) return;
    const inverted = rectangle.y + rectangle.height < multiSelectBox.y;
    if (inverted) {
      y = multiSelectBox.y + BOX_BUFFER / 2;
      height = 1;
    } else {
      const innerRectInverted = height < 0;
      if (innerRectInverted) {
        height = Math.max(height, 1);
      }
    }
  };
  const checkBoundsFromBottomChange = () => {
    if (!multiSelectBox) return;
    const inverted = rectangle.y + BOX_BUFFER / 2 > multiSelectBox.y + multiSelectBox.height;
    if (inverted) {
      y = multiSelectBox.y + multiSelectBox.height - BOX_BUFFER / 2;
      height = 1;
    } else {
      const topGreaterThanBoxTop = rectangle.y - BOX_BUFFER / 2 - 1 <= multiSelectBox.y;
      if (topGreaterThanBoxTop) {
        y = Math.max(multiSelectBox.y + BOX_BUFFER / 2, rectangle.y);
      }
      height = Math.max(height, 1);
    }
  };
  switch (selectedVertexType) {
    case VERTEX_TYPE.TOP_LEFT:
      width = rectangle.x + rectangle.width - newPos[0];
      height = rectangle.y + rectangle.height - newPos[1];
      //Case to handle when TOP_LEFT vertex crosses the TOP_RIGHT (for width) or/and BOTTOM_RIGHT (for height) vertex so need to change the TOP_LEFT vertex.
      x = width < 0 ? newPos[0] + width : newPos[0];
      y = height < 0 ? newPos[1] + height : newPos[1];
      checkBoundsFromLeftChange();
      checkBoundsFromTopChange();
      break;
    case VERTEX_TYPE.TOP:
      height = rectangle.y + rectangle.height - newPos[1];
      //Case to handle when TOP vertex crosses the BOTTOM vertex (for height) so need to change the TOP_LEFT vertex.
      y = height < 0 ? newPos[1] + height : newPos[1];
      checkBoundsFromTopChange();
      break;
    case VERTEX_TYPE.TOP_RIGHT:
      y = newPos[1];
      width = newPos[0] - rectangle.x;
      height = rectangle.y + rectangle.height - newPos[1];
      //Case to handle when TOP_RIGHT vertex crosses the TOP_LEFT (width) or/and BOTTOM_LEFT (for height) vertex so need to change the TOP_LEFT vertex.
      x = width < 0 ? x + width : x;
      y = height < 0 ? newPos[1] + height : newPos[1];
      checkBoundsFromRightChange();
      checkBoundsFromTopChange();
      break;
    case VERTEX_TYPE.RIGHT:
      width = newPos[0] - rectangle.x;
      //Case to handle when RIGHT vertex crosses the LEFT vertex (for width) so need to change the TOP_LEFT vertex.
      x = width < 0 ? x + width : x;
      checkBoundsFromRightChange();
      break;
    case VERTEX_TYPE.BOTTOM_RIGHT:
      width = newPos[0] - rectangle.x;
      height = newPos[1] - rectangle.y;
      //Case to handle when BOTTOM_RIGHT vertex crosses the TOP_RIGHT (for height) or/and BOTTOM_LEFT (for width) vertex so need to change the TOP_LEFT vertex.
      x = width < 0 ? x + width : x;
      y = height < 0 ? y + height : y;
      checkBoundsFromRightChange();
      checkBoundsFromBottomChange();
      break;
    case VERTEX_TYPE.BOTTOM:
      height = newPos[1] - rectangle.y;
      //Case to handle when BOTTOM vertex crosses TOP vertex (for height) so need to change the TOP_LEFT vertex.
      y = height < 0 ? y + height : y;
      checkBoundsFromBottomChange();
      break;
    case VERTEX_TYPE.BOTTOM_LEFT:
      width = rectangle.x + rectangle.width - newPos[0];
      height = newPos[1] - rectangle.y;
      //Case to handle when BOTTOM_LEFT vertex crosses TOP_LEFT (for height) or/ans BOTTOM_RIGHT (for width) vertex so need to change the TOP_LEF vertex.
      x = width < 0 ? newPos[0] + width : newPos[0];
      y = height < 0 ? y + height : y;
      checkBoundsFromLeftChange();
      checkBoundsFromBottomChange();
      break;
    case VERTEX_TYPE.LEFT:
      width = rectangle.x + rectangle.width - newPos[0];
      //Case to handle when LEFT vertex crosses RIGHT vertex (for width) so need to change the TOP_LEFT vertex.
      x = width < 0 ? newPos[0] + width : newPos[0];
      checkBoundsFromLeftChange();
      break;
    default:
      break;
  }
  return {
    x,
    y,
    //No need for negative height and width, already changed the TOP_LEFT vertex accordingly.
    height: Math.abs(height),
    width: Math.abs(width),
    rotationAngle
  };
};